import { Box, Stack, Typography, Button, Chip, Link as MuiLink } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  FormOutlined,
  ExportOutlined,
  CheckCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';
import { ErrorAction, ErrorSeverity } from '../types';

interface Props {
  text: string;
  severity?: ErrorSeverity;
  applicationId?: string;
  applicationNumber?: number;
  actions?: ErrorAction[];
  resolved?: boolean;
  onOpenApplication?: (applicationId: string) => void;
  onRetry?: (command: string) => void;
  onDismiss?: () => void;
}

const ErrorCard = ({
  text,
  severity = 'error',
  applicationId,
  applicationNumber,
  actions,
  resolved,
  onOpenApplication,
  onRetry,
  onDismiss,
}: Props) => {
  const theme = useTheme();
  const isWarning = severity === 'warning';
  const accent = isWarning ? theme.palette.warning.main : theme.palette.error.main;
  const Icon = isWarning ? ExclamationCircleOutlined : CloseCircleOutlined;

  const renderedActions = actions?.filter((a) => a.kind !== 'dismiss') ?? [];
  const hasDismiss = actions?.some((a) => a.kind === 'dismiss');

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 1.75,
        mb: 2,
        bgcolor: alpha(accent, 0.06),
        border: `1px solid ${alpha(accent, 0.22)}`,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.25, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(accent, 0.15),
        }}>
          <Icon style={{ fontSize: 17, color: accent }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: '0.9375rem',
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
            }}
          >
            {text}
          </Typography>

          {applicationNumber !== undefined && applicationId && (
            <MuiLink
              component={RouterLink}
              to={`/applications/${applicationId}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                mt: 0.75,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: accent,
              }}
            >
              Application #{applicationNumber}
              <ExportOutlined style={{ fontSize: 11 }} />
            </MuiLink>
          )}
        </Box>
      </Stack>

      {resolved ? (
        <Chip
          icon={<CheckCircleFilled style={{ fontSize: 11 }} />}
          label="Action taken"
          size="small"
          sx={{
            mt: 1.5,
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            color: theme.palette.text.disabled,
            '& .MuiChip-icon': { color: theme.palette.text.disabled },
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ) : (
        renderedActions.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.75 }}>
            {renderedActions.map((action, idx) => {
              const isPrimary = idx === 0;
              if (action.kind === 'open_application') {
                return (
                  <Button
                    key={`open-${idx}`}
                    component={RouterLink as any}
                    to={`/applications/${action.applicationId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant={isPrimary ? 'contained' : 'outlined'}
                    startIcon={<FormOutlined style={{ fontSize: 13 }} />}
                    onClick={() => onOpenApplication?.(action.applicationId)}
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      py: 0.625,
                      px: 1.5,
                      textTransform: 'none',
                      ...(isPrimary
                        ? {
                            bgcolor: accent,
                            color: '#fff',
                            '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' },
                          }
                        : {
                            borderColor: alpha(accent, 0.5),
                            color: accent,
                            '&:hover': {
                              borderColor: accent,
                              bgcolor: alpha(accent, 0.06),
                            },
                          }),
                    }}
                  >
                    {action.label ?? 'Open application'}
                  </Button>
                );
              }
              if (action.kind === 'retry') {
                return (
                  <Button
                    key={`retry-${idx}`}
                    size="small"
                    variant={isPrimary ? 'contained' : 'outlined'}
                    startIcon={<ReloadOutlined style={{ fontSize: 13 }} />}
                    onClick={() => onRetry?.(action.command)}
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      py: 0.625,
                      px: 1.5,
                      textTransform: 'none',
                      ...(isPrimary
                        ? {
                            bgcolor: accent,
                            color: '#fff',
                            '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' },
                          }
                        : {
                            borderColor: alpha(accent, 0.5),
                            color: accent,
                            '&:hover': {
                              borderColor: accent,
                              bgcolor: alpha(accent, 0.06),
                            },
                          }),
                    }}
                  >
                    {action.label ?? 'Retry'}
                  </Button>
                );
              }
              return null;
            })}
            {(hasDismiss || !renderedActions.length) && (
              <Button
                size="small"
                startIcon={<CloseOutlined style={{ fontSize: 13 }} />}
                onClick={onDismiss}
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  py: 0.625,
                  px: 1.5,
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                }}
              >
                Dismiss
              </Button>
            )}
          </Stack>
        )
      )}
    </Box>
  );
};

export default ErrorCard;
